import { useState, useEffect, useMemo, useCallback, useRef } from 'react';
import { checkSkuDuplicates, DuplicateStrategy } from '@/services/importService';
import { Mapping } from '@/types/import';
import { logError } from '@/utils/errorHandler';

export interface SkuValidationResult {
  // SKUs found in the file
  allSkus: string[];
  // SKUs that appear multiple times within the file
  inFileDuplicates: string[];
  // SKUs that already exist in the database
  existingDuplicates: string[];
  // Count of unique vs duplicate SKUs
  stats: {
    total: number;
    unique: number;
    inFileDuplicates: number;
    existingDuplicates: number;
  };
  // Loading and error states
  isValidating: boolean;
  validationError?: string;
  // Convenience property for checking if any duplicates exist
  hasAnyDuplicates: boolean;
  // Whether a SKU column is currently mapped
  hasSkuColumn: boolean;
  // Whether existing duplicates should be treated as warnings (based on strategy)
  shouldWarnOnExisting: boolean;
  // Manual validation trigger
  validate: () => void;
}

export interface UseSkuValidationOptions {
  // Preview data from uploaded file (for backward compatibility)
  previewData?: Array<Record<string, any>>;
  // Full data from uploaded file (for complete validation)
  fullData?: Array<Record<string, any>>;
  // Current column mapping
  mapping?: Mapping;
  // Whether to automatically validate when inputs change
  autoValidate?: boolean;
  // Debounce time in milliseconds for auto-validation
  debounceMs?: number;
  // Import duplicate strategy for context-aware validation
  duplicateStrategy?: DuplicateStrategy;
}

/**
 * Custom hook for validating SKUs during the import process
 * Provides real-time validation with intelligent duplicate detection
 */
export function useSkuValidation({
  previewData = [],
  fullData,
  mapping = {},
  autoValidate = true,
  debounceMs = 500,
  duplicateStrategy = 'overwrite',
}: UseSkuValidationOptions): SkuValidationResult {
  const [validationState, setValidationState] = useState({
    allSkus: [] as string[],
    inFileDuplicates: [] as string[],
    existingDuplicates: [] as string[],
    stats: { total: 0, unique: 0, inFileDuplicates: 0, existingDuplicates: 0 },
    isValidating: false,
    hasAnyDuplicates: false,
    validationError: undefined as string | undefined,
  });

  // Track last validation to prevent unnecessary re-validations
  const lastValidationRef = useRef<string>('');
  const timeoutRef = useRef<NodeJS.Timeout>();

  // Determine if there's a SKU column mapped
  const hasSkuColumn = useMemo(() => {
    return Object.values(mapping || {}).includes('sku');
  }, [mapping]);

  // Extract SKUs from data based on current mapping
  const extractSkus = useMemo(() => {
    // Use fullData if available for complete validation, otherwise fall back to previewData
    const dataToProcess = fullData && fullData.length > 0 ? fullData : previewData;

    if (!dataToProcess || dataToProcess.length === 0 || !hasSkuColumn) {
      return [];
    }

    // Find the column mapped to 'sku'
    const skuColumnName = Object.keys(mapping || {}).find(key => mapping![key] === 'sku');
    if (!skuColumnName) {
      return [];
    }

    // Extract SKUs from all rows
    return dataToProcess
      .map(row => row[skuColumnName])
      .filter(sku => sku && typeof sku === 'string' && sku.trim().length > 0)
      .map(sku => sku.trim());
  }, [previewData, fullData, mapping, hasSkuColumn]);

  // Find in-file duplicates
  const findInFileDuplicates = useMemo(() => {
    const skuCounts = new Map<string, number>();

    extractSkus.forEach(sku => {
      skuCounts.set(sku, (skuCounts.get(sku) || 0) + 1);
    });

    return Array.from(skuCounts.entries())
      .filter(([_, count]) => count > 1)
      .map(([sku, _]) => sku);
  }, [extractSkus]);

  // Determine if existing duplicates should be treated as warnings
  const shouldWarnOnExisting = useMemo(() => {
    // Only warn on existing duplicates if strategy is 'abort'
    // For 'overwrite' and 'skip', existing duplicates are expected behavior
    return duplicateStrategy === 'abort';
  }, [duplicateStrategy]);

  // Create a validation key to determine if validation is actually needed
  const validationKey = useMemo(() => {
    const mappingEntries = Object.entries(mapping || {}).sort();
    const skuColumnName = mappingEntries.find(([_, value]) => value === 'sku')?.[0] || null;
    const dataLength = (fullData && fullData.length > 0 ? fullData : previewData).length;

    // Only include the essential parts for validation
    return JSON.stringify({
      hasSkuColumn,
      skuColumnName,
      dataLength,
      // Use a stable representation of mapping instead of the object reference
      mappingStr: mappingEntries.map(([k, v]) => `${k}:${v}`).join(','),
    });
  }, [hasSkuColumn, mapping, previewData, fullData]);

  // Validation function
  const validateSkus = useCallback(async () => {
    // Check if validation is actually needed
    if (lastValidationRef.current === validationKey) {
      return; // Skip if no changes
    }

    if (!hasSkuColumn || extractSkus.length === 0) {
      setValidationState({
        allSkus: [],
        inFileDuplicates: [],
        existingDuplicates: [],
        stats: {
          total: 0,
          unique: 0,
          inFileDuplicates: 0,
          existingDuplicates: 0,
        },
        isValidating: false,
        validationError: undefined,
        hasAnyDuplicates: false,
      });
      lastValidationRef.current = validationKey;
      return;
    }

    setValidationState(prev => ({
      ...prev,
      isValidating: true,
    }));

    try {
      const uniqueSkus = Array.from(new Set(extractSkus));
      const duplicateCheckResult = await checkSkuDuplicates(uniqueSkus);

      setValidationState({
        allSkus: extractSkus,
        inFileDuplicates: findInFileDuplicates,
        existingDuplicates: duplicateCheckResult.duplicates,
        stats: {
          total: extractSkus.length,
          unique: uniqueSkus.length,
          inFileDuplicates: findInFileDuplicates.length,
          existingDuplicates: duplicateCheckResult.duplicates.length,
        },
        isValidating: false,
        validationError: duplicateCheckResult.error,
        hasAnyDuplicates:
          findInFileDuplicates.length > 0 || duplicateCheckResult.duplicates.length > 0,
      });

      // Update the last validation key
      lastValidationRef.current = validationKey;
    } catch (error) {
      logError(error instanceof Error ? error : new Error(String(error)), {
        component: 'useSkuValidation',
        action: 'validateSkus',
        metadata: {
          skuCount: extractSkus.length,
          context: 'sku_validation_error',
        },
      });
      setValidationState(prev => ({
        ...prev,
        isValidating: false,
        validationError: 'Unexpected error during SKU validation',
      }));
      lastValidationRef.current = validationKey;
    }
  }, [validationKey, extractSkus, findInFileDuplicates, hasSkuColumn]);

  // Manual validation function
  const validate = useCallback(() => {
    // Force validation by clearing the last validation key
    lastValidationRef.current = '';
    validateSkus();
  }, [validateSkus]);

  // Auto-validation with debouncing
  useEffect(() => {
    if (!autoValidate) return;

    // Clear any existing timeout
    if (timeoutRef.current) {
      clearTimeout(timeoutRef.current);
    }

    // Only schedule validation if something actually changed
    if (lastValidationRef.current !== validationKey) {
      timeoutRef.current = setTimeout(() => {
        validateSkus();
      }, debounceMs);
    }

    return () => {
      if (timeoutRef.current) {
        clearTimeout(timeoutRef.current);
      }
    };
  }, [validationKey, autoValidate, debounceMs, validateSkus]);

  return {
    ...validationState,
    hasSkuColumn,
    shouldWarnOnExisting,
    validate,
  };
}
